import React, { useRef, useState, useCallback, useEffect, MouseEvent } from "react";
import { ReactWaves } from "components/ReactWaves";
import { useAudioStateContext } from "hooks/useAudioStateContext";
import { WaveWrapperSC } from "./styled";
import { useAudioHandlesContext } from "hooks/useAudioHandlesContext";
import { AudioTrack } from "providers/AudioProvider/types";
import { useAudioPlay } from "./hooks/useAudioPlay";
import { WaveSurfer } from "types";
import { useAudioData } from "./hooks/useAudioData";
import { computePeaks, Peaks } from "utils/audioPeaks";
import { guardSeekTo } from "utils/safeWavesurfer";
import {
    isClipWanted,
    publishClipState,
    registerClipMedia,
    subscribeAwaited,
    subscribeClips,
} from "utils/clipRegistry";

/** readyState at which the element will play through without stopping to buffer. */
const HAVE_FUTURE_DATA = 3;

/** Buckets for a row's waveform. The rows are narrow; a folder-length resolution is wasted here. */
const ROW_PEAK_BUCKETS = 512;

interface PlayerTranslated2Props {
    length: number;
    width: number;
    id: string;
    ind: number;
    handlePlayNext: (ind: number) => void;
    mp3: string;
    end_rus_milli?: number;
    start_rus_milli?: number;
    trimmed?: number | null;
    start: number;
    prevTrack?: AudioTrack;
    options?: {
        barHeight: number;
        barRadius: number;
        cursorWidth: number;
        height: number;
        hideScrollbar: boolean;
        progressColor: string;
        responsive: boolean;
        waveColor: string;
        interact: boolean;
        dragSelection: boolean;
        backend?: string;
    };
}

export const AudioPlayerTrack: React.FC<PlayerTranslated2Props> = React.memo(
    ({
        length,
        width,
        id,
        ind,
        handlePlayNext,
        mp3,
        end_rus_milli,
        start_rus_milli,
        trimmed,
        start,
        prevTrack,
        options = {
            barHeight: 2,
            barRadius: 3,
            cursorWidth: 0,
            height: 80,
            hideScrollbar: true,
            progressColor: "#ff7606",
            responsive: true,
            waveColor: "#D1D6DA",
            interact: false,
            dragSelection: false,
            backend: "MediaElement",
            overflow: "visible",
        },
    }) => {
        const state = useAudioStateContext();
        const handles = useAudioHandlesContext();
        const wavesRef = useRef<any>(null);
        const wavesRefWrapper = useRef<HTMLDivElement>(null);

        const [wavesurfer, setWavesurfer] = useState<WaveSurfer | null>(null);
        const [pos, setPos] = useState<number>(0);

        const [isFinish, setIsFinish] = useState(false);

        // Звук готовится, только когда он нужен: строка видна или её просит расписание. Раньше в
        // условии был ещё и `state.playing` — то есть при старте воспроизведения СРАЗУ все реплики
        // папки (на лекции 8779, папка 25 — 72 штуки) начинали готовиться одновременно, и та
        // единственная, которая нужна сейчас, стояла в общей очереди наравне с остальными.
        const rowRef = useRef<HTMLDivElement>(null);
        const [isNear, setIsNear] = useState(false);
        useEffect(() => {
            const node = rowRef.current;
            if (!node || isNear) return;
            if (typeof IntersectionObserver === "undefined") {
                setIsNear(true); // старый браузер — ведём себя как раньше
                return;
            }
            const io = new IntersectionObserver(
                (entries) => {
                    if (entries.some((e) => e.isIntersecting)) {
                        setIsNear(true);
                        io.disconnect();
                    }
                },
                // Запас в два экрана: к моменту, когда строка доедет до глаз, волна уже нарисована.
                { root: null, rootMargin: "200% 0px", threshold: 0 },
            );
            io.observe(node);
            return () => io.disconnect();
        }, [isNear]);

        // Расписание заранее просит подготовить ближайшие реплики — это и есть прогрев.
        const [isWanted, setIsWanted] = useState<boolean>(() => isClipWanted(id));
        useEffect(() => subscribeClips(() => setIsWanted(isClipWanted(id))), [id]);

        const { isLoading, loadError, vaw } = useAudioData({
            id,
            mp3,
            enabled: isNear || isWanted || state.playSavedTrack,
        });

        /**
         * THE ELEMENT THAT PLAYS THIS CUE — ours, not the library's.
         *
         * It used to be created by WaveSurfer, which meant it appeared only once the waveform had
         * been drawn, and could be replaced or removed underneath us on the library's schedule.
         * Now the row renders it, tags it and reports its readiness; WaveSurfer is handed the same
         * element purely to draw on (`mediaElt` below). Exactly one element per cue, and it exists
         * as soon as there are bytes for it.
         */
        const [media, setMedia] = useState<HTMLAudioElement | null>(null);
        const mediaRef = useCallback((el: HTMLAudioElement | null) => setMedia(el), []);

        useEffect(() => {
            if (!media) return;

            const check = () => {
                if (media.readyState >= HAVE_FUTURE_DATA) publishClipState(id, "ready");
            };
            const fail = () => {
                const code = media.error?.code;
                publishClipState(id, "error", code ? `media error ${code}` : "media error");
            };
            // media.load() empties the element and starts again from nothing. WaveSurfer does
            // exactly that when it is first given this element (dist/index.es.js:2125), so
            // readiness has to be given back rather than merely assumed to persist — a registry
            // that says "ready" about an element at readyState 0 is worse than one that says
            // nothing, because the schedule would let the lecture past it.
            const emptied = () => publishClipState(id, "loading");

            // Событие могло произойти до того, как мы подписались: элемент с blob-источником
            // нередко готов уже в этом же кадре, и тогда больше ни одно событие не придёт.
            check();
            media.addEventListener("canplaythrough", check);
            media.addEventListener("canplay", check);
            media.addEventListener("loadeddata", check);
            media.addEventListener("error", fail);
            media.addEventListener("emptied", emptied);
            registerClipMedia(id, media);

            return () => {
                media.removeEventListener("canplaythrough", check);
                media.removeEventListener("canplay", check);
                media.removeEventListener("loadeddata", check);
                media.removeEventListener("error", fail);
                media.removeEventListener("emptied", emptied);
                registerClipMedia(id, null);
            };
        }, [media, id, vaw]);

        useAudioPlay({ media, start, id, length });

        /**
         * Peaks for the picture, never for the sound.
         *
         * WaveSurfer draws nothing from a MediaElement backend unless peaks are supplied — and if
         * they are NOT supplied it reads the whole file back and decodes it itself just to draw
         * (dist/index.es.js:4850). That decode, times a folder of voicings, is the several seconds
         * a clip used to spend becoming playable. So peaks are computed here instead: only for
         * rows actually on screen, at most two at a time (utils/audioPeaks), and never on the path
         * to first sound. Until they arrive the row draws flat, which costs nobody anything.
         */
        const [peaks, setPeaks] = useState<Peaks>([]);
        useEffect(() => {
            if (!vaw || !isNear) return;
            const controller = new AbortController();
            let cancelled = false;
            computePeaks(vaw, ROW_PEAK_BUCKETS, controller.signal).then((result) => {
                if (!cancelled && result.length) setPeaks(result);
            });
            return () => {
                cancelled = true;
                controller.abort();
            };
        }, [vaw, isNear]);

        /**
         * WHEN THE WAVE MAY ATTACH ITSELF TO THE ELEMENT THAT PLAYS.
         *
         * Handing an element to WaveSurfer costs one media.load() — it empties the element and
         * starts it again (dist/index.es.js:2125), and there is no way to ask it not to while the
         * element must stay prepared. Doing that to a clip that is sounding would cut it off, so
         * the wave waits for a moment when the cue is not the current one. It also waits for the
         * peaks, so they are handed over once: changing `audioPeaks` afterwards makes the library
         * re-load, and that is the same interruption arriving by a different door.
         *
         * A latch, not a condition: once drawn, the wave stays drawn.
         */
        const isCurrent = state.playingFragment === id;
        const [waveOn, setWaveOn] = useState(false);
        useEffect(() => {
            if (waveOn || isCurrent) return;
            if (isNear && peaks.length > 0) setWaveOn(true);
        }, [waveOn, isCurrent, isNear, peaks]);

        const onReady = useCallback(({ wavesurfer }: { wavesurfer: WaveSurfer }) => {
            // THE WAVEFORM DRAWS. IT DOES NOT DECIDE WHAT SOUNDS.
            //
            // react-waves runs `nextProps.playing ? wavesurfer.play() : wavesurfer.pause()` from
            // componentWillReceiveProps — on every prop change, not only when the value changes
            // (@dschoon/react-waves dist/index.es.js:9336). Two consequences, both of which were
            // reported as bugs and neither of which can be fixed from outside:
            //
            //   * play() on an element that has ended rewinds it, so a fragment replayed itself
            //     on the first render after finishing — measured at 9-20 replays a minute;
            //   * anything this hook does to the element is undone by the next render, so
            //     pausing a fragment that should not be sounding did not keep it paused.
            //
            // Both disappear once the library stops driving playback. Its play/pause become
            // no-ops here, the `playing` prop is pinned false, and useAudioPlay works the media
            // element directly. seekTo is left alone — it only resumes an element that was
            // already running, and the waveform still needs it to draw the playhead.
            //
            // Captured from `ready` rather than `loading`: supplying peaks is what stops the
            // library reading the file, and `loading` is emitted BY that read, so with peaks it
            // never fires at all.
            wavesurfer.play = () => Promise.resolve();
            wavesurfer.pause = () => undefined;
            // seekTo stays alive for drawing the playhead, but must never be able to throw or
            // write NaN into the element's clock: see utils/safeWavesurfer.
            guardSeekTo(wavesurfer);
            setWavesurfer(wavesurfer);
        }, []);

        // A CLICK ON THE WAVE MEANS THE SAME AS A CLICK ON THE ROW: this voicing, from the top.
        //
        // There used to be a second meaning layered on the same click: the row's mousedown seeked
        // to the cue's start, and then this element's click handler re-seeked to the clicked
        // word within the clip — so every click on a waveform started the voicing part-way in.
        // For the translator that read as "it doesn't play from the beginning", and it is the
        // wave that fills the row, so it is where clicks actually land. One click, one meaning.
        // Entering a cue at a specific word is still there: click that moment on the ORIGINAL
        // waveform on the left, and the aligned start does exactly that.

        useEffect(() => {
            if (state.playingFragment && state.playingFragment !== id) {
                setPos(0);
                if (wavesurfer && wavesurfer.isReady) {
                    wavesurfer.seekTo(0);
                }
            }
        }, [state.playingFragment]);

        // Restart counter: seek to 0 when the same track is re-triggered via shortcut
        useEffect(() => {
            if (state.playingFragmentRestart > 0 && state.playingFragment === id) {
                setPos(0);
                if (wavesurfer && wavesurfer.isReady) {
                    wavesurfer.seekTo(0);
                }
            }
        }, [state.playingFragmentRestart]);

        // Reaching the end of a clip is not an instruction to play anything else. The schedule
        // moves on when the ORIGINAL reaches the next cue, which is a different moment: the
        // translation is shorter than the slot it sits in, so the lecturer is usually still
        // talking. Chaining here is what used to start the next voicing early.
        const handleFinish = () => {
            setIsFinish(true);
        };

        useEffect(() => {
            if (!isFinish) return;
            // Reviewing saved tracks is the one mode with no original to follow, so finishing IS
            // the cue to move on there; the provider picks the next changed track once this
            // clears (audioProvider.tsx:406-415).
            if (state.playSavedTrack) {
                handles.setPlayingFragment(null);
            }
            setPos(0);
            setIsFinish(false);
        }, [isFinish]);

        /** Is the lecture stopped for THIS cue right now? Then say so where the wave would be. */
        const [isAwaited, setIsAwaited] = useState(false);
        useEffect(() => subscribeAwaited((awaited) => setIsAwaited(awaited === id)), [id]);

        // Prepare final options for ReactWaves
        const waveOptions = {
            ...options,
            audioRate: state.playbackRate,
            // The element belongs to React. WaveSurfer's default is to remove the media element
            // from the DOM when it is destroyed (dist/index.es.js:2408,
            // `removeMediaElementOnDestroy`) — which, for an element it did not create, means
            // tearing a node out from under React and crashing the next unmount.
            removeMediaElementOnDestroy: false,
        };

        // Тот же ref и тот же размер во всех состояниях: наблюдателю нужно, за чем следить, а
        // списку — чтобы строки не прыгали, когда волна дорисуется.
        if (loadError) {
            return (
                <div ref={rowRef} style={{ paddingTop: "0.26em", width, color: "red" }}>
                    Error loading audio: {loadError}
                </div>
            );
        }

        if (!vaw) {
            return (
                <div ref={rowRef} style={{ paddingTop: "0.26em", width, height: options.height }}>
                    <div className="wave-loading">
                        {isAwaited ? "Загрузка озвучки…" : isLoading ? "Loading..." : ""}
                    </div>
                </div>
            );
        }

        return (
            <div ref={rowRef} style={{ width }}>
                <div
                    style={{
                        top: 0,
                        bottom: 0,
                        left: 0,
                        right: 0,
                        zIndex: 1,
                        position: "absolute",
                    }}
                    ref={wavesRefWrapper}
                />

                {/* Звучит именно этот элемент. Он же отдаётся волне через mediaElt. */}
                <audio
                    ref={mediaRef}
                    src={vaw}
                    preload="auto"
                    // Помечен сразу, а не в эффекте: originalPositionMs() ищет оригинал как
                    // `audio:not([data-cue])`, и непомеченная озвучка успела бы сойти за него.
                    data-cue={id}
                    style={{ display: "none" }}
                />

                {media && waveOn && (
                    <WaveWrapperSC>
                        <ReactWaves
                            ref={wavesRef}
                            mediaElt={media}
                            audioPeaks={peaks}
                            className="react-waves react-waves-v"
                            options={waveOptions}
                            volume={(state.volumeTranslated || 0) / 100}
                            zoom={(100 - (state.zoomLevel || 0)) / 100}
                            pos={pos}
                            playing={false}
                            onReady={onReady}
                            onFinish={handleFinish}
                        />
                    </WaveWrapperSC>
                )}
            </div>
        );
    },
);
